import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, KeyRound, ArrowLeft, MailCheck } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSEO } from "@/components/common/SEO";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const FORGOT_COPY = {
  ca: { title: "No recordes la contrasenya?", subtitle: "Introdueix el teu correu i t'enviarem un enllaç per crear-ne una de nova.",
        email: "Correu electrònic", submit: "Envia'm l'enllaç", back: "Torna al login",
        sent: "T'hem enviat un correu (si aquest compte existeix) amb les instruccions per restablir la contrasenya. Comprova la safata d'entrada i el correu brossa.",
        link: "No recordo la contrasenya" },
  es: { title: "¿No recuerdas la contraseña?", subtitle: "Introduce tu correo y te enviaremos un enlace para crear una nueva.",
        email: "Correo electrónico", submit: "Enviarme el enlace", back: "Volver al login",
        sent: "Te hemos enviado un correo (si esa cuenta existe) con las instrucciones para restablecer la contraseña. Revisa la bandeja de entrada y la carpeta de spam.",
        link: "No recuerdo la contraseña" },
  en: { title: "Forgot your password?", subtitle: "Enter your email and we'll send you a link to create a new one.",
        email: "Email address", submit: "Send me the link", back: "Back to login",
        sent: "We've sent an email (if this account exists) with instructions to reset your password. Check your inbox and spam folder.",
        link: "Forgot your password?" },
  fr: { title: "Mot de passe oublié ?", subtitle: "Saisissez votre e-mail et nous vous enverrons un lien pour en créer un nouveau.",
        email: "Adresse e-mail", submit: "Envoyer le lien", back: "Retour à la connexion",
        sent: "Nous vous avons envoyé un e-mail (si ce compte existe) avec les instructions. Vérifiez votre boîte de réception et le dossier spam.",
        link: "Mot de passe oublié ?" },
};

export default function Login() {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const fc = FORGOT_COPY[lang] || FORGOT_COPY.ca;
  useSEO({ lang, title: `${t("login.title")} | HO-SYS`, description: t("login.subtitle"), path: "/login" });

  useEffect(() => { if (user && user.role) navigate(`/${lang}/admin`); }, [user, lang, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? `/${lang}/admin` : `/${lang}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("login.error"));
    } finally { setLoading(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, lang });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally { setLoading(false); }
  };

  const inputCls = "w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:border-neon/60 transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40" />
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[300px] bg-neon/15 blur-[120px] rounded-full" />
      <div className="relative w-full max-w-md">
        <div className="flex justify-center mb-8"><Link to={`/${lang}`}><Logo variant="full" height={160} /></Link></div>

        {mode === "login" && (
          <form onSubmit={submit} data-testid="login-form" className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-8">
            <h1 className="font-display text-2xl font-bold text-white text-center">{t("login.title")}</h1>
            <p className="text-white/50 text-sm text-center mt-1 mb-7">{t("login.subtitle")}</p>
            <div className="space-y-4">
              <input data-testid="login-email" type="email" required placeholder={t("login.email")} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <input data-testid="login-password" type="password" required placeholder={t("login.password")} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            </div>
            {error && <p data-testid="login-error" className="mt-4 text-sm text-red-400">{error}</p>}
            <button data-testid="login-submit" type="submit" disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60">
              {loading ? t("common.loading") : t("login.submit")} <LogIn className="w-4 h-4" />
            </button>
            <button type="button" data-testid="login-forgot-link" onClick={() => { setMode("forgot"); setError(""); setForgotSent(false); }}
              className="mt-5 w-full text-center text-sm text-white/50 hover:text-neon transition-colors inline-flex items-center justify-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" /> {fc.link}
            </button>
          </form>
        )}

        {mode === "forgot" && (
          <form onSubmit={submitForgot} data-testid="forgot-form" className="rounded-2xl border border-white/10 bg-black/60 backdrop-blur-xl p-8">
            <h1 className="font-display text-2xl font-bold text-white text-center">{fc.title}</h1>
            <p className="text-white/50 text-sm text-center mt-1 mb-7">{fc.subtitle}</p>

            {forgotSent ? (
              <div data-testid="forgot-sent" className="rounded-xl bg-neon/[0.06] border border-neon/30 p-4 flex items-start gap-3">
                <MailCheck className="w-5 h-5 text-neon shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm leading-relaxed">{fc.sent}</p>
              </div>
            ) : (
              <>
                <input data-testid="forgot-email" type="email" required placeholder={fc.email} value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                {error && <p data-testid="forgot-error" className="mt-4 text-sm text-red-400">{error}</p>}
                <button data-testid="forgot-submit" type="submit" disabled={loading}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-neon px-6 py-3.5 font-semibold text-black hover:bg-white transition-colors disabled:opacity-60">
                  {loading ? t("common.loading") : fc.submit}
                </button>
              </>
            )}
            <button type="button" data-testid="forgot-back" onClick={() => { setMode("login"); setError(""); }}
              className="mt-5 w-full text-center text-sm text-white/50 hover:text-neon transition-colors inline-flex items-center justify-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> {fc.back}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
